import { Box, IconButton, Typography } from "@mui/material";
import { isFulfilled } from "@reduxjs/toolkit";
import { format } from "date-fns";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../../../app/hooks";
import {
  GroupedTable,
  GroupedTableColumn,
} from "../../../common/components/functional/GroupedTable/GroupedTable";
import { leadingZerosDateFormat } from "../../../common/helpers/formatDateTime";
import { ReactComponent as ClipboardIcon } from "../../../common/icons/clipboardFilled.svg";
import { Hits, Relation } from "../../../common/types/Paginated";
import { ItemGroup, ItemGroupLoadingState } from "../../invoices/invoiceAPI";
import {
  TalentJobApplication,
  TalentJobApplications,
  TalentJobApplicationRowItem,
} from "../../talents/talentAPI";
import {
  fetchCandidateIntroductions,
  fetchTalentJobApplications,
} from "../../talents/talentsSlice";
import {
  ALL_WITH_ORDER_INTRODUCTION_STATUSES,
  TalentIntroduction,
  TalentIntroducedToCustomerStatus,
} from "../../talents/types/queries";
import { AssignmentCoverLetter } from "../assignmentCoverLetter/AssignmentCoverLetter";
import { IntroductionStatus } from "./IntroductionStatus";
import { TalentIntroductions } from "./TalentIntroductions";

type Props = {
  talentId: string;
};

export const JobApplications = ({ talentId }: Props) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const columns: GroupedTableColumn[] = [
    {
      fieldName: "title",
      displayName: "jobAd",
      width: "260px",
      disableTooltip: true,
    },
    {
      fieldName: "location",
      displayName: "location",
      width: "255px",
      disableTooltip: true,
    },
    {
      fieldName: "startDate",
      displayName: "jobAds:startDate",
      width: "90px",
      disableTooltip: true,
    },
    {
      fieldName: "endDate",
      displayName: "jobAds:endDate",
      width: "90px",
      disableTooltip: true,
    },
    {
      fieldName: "customerCompanyName",
      displayName: "customer",
      width: "245px",
      disableTooltip: true,
    },
    {
      fieldName: "contactPersonName",
      displayName: "contactPerson",
      width: "200px",
      disableTooltip: true,
    },
    {
      fieldName: "appliedAt",
      width: "100px",
      displayName: "jobAds:appliedAt",
      disableTooltip: true,
    },
    {
      fieldName: "status",
      width: "150px",
      displayName: "status",
      disableTooltip: true,
    },
  ];

  const [applications, setApplications] = useState<TalentJobApplications>({
    ongoing: [],
    ended: [],
  });
  const [withOrderIntroductions, setWithOrderIntroductions] = useState<
    TalentIntroduction[]
  >([]);
  const [hits, setHits] = useState<Hits>({
    value: 0,
    relation: Relation.DEFAULT,
  });

  const appliedBundleIds = useMemo(
    () =>
      new Set(
        [...applications.ongoing, ...applications.ended].flatMap(
          ({ bundleId }) => (bundleId ? [bundleId] : []),
        ),
      ),
    [applications],
  );

  const introductionsByBundleId = useMemo(
    () =>
      new Map(
        withOrderIntroductions.flatMap((introduction) =>
          introduction.bundleId
            ? [[introduction.bundleId, introduction] as const]
            : [],
        ),
      ),
    [withOrderIntroductions],
  );

  const [coverLetterModalProps, setCoverLetterModalProps] = useState({
    open: false,
    talentId: "",
    jobAdId: "",
    talentFullName: "",
  });

  const openCoverLetterDialog = useCallback(
    (jobAdId: string) => {
      setCoverLetterModalProps({
        open: true,
        talentId,
        jobAdId,
        talentFullName: "",
      });
    },
    [talentId],
  );

  const mapItemToRow = useCallback(
    (item: TalentJobApplication) => {
      const introduction = item.bundleId
        ? introductionsByBundleId.get(item.bundleId)
        : undefined;
      const status =
        item.eezyAccepted === false ? (
          <IntroductionStatus
            status={TalentIntroducedToCustomerStatus.EezyRejected}
            rejectionReason={item.eezyRejectionReason}
            rejectionComment={item.eezyRejectionComment}
          />
        ) : introduction ? (
          <IntroductionStatus {...introduction} />
        ) : item.eezyAccepted === true ? (
          t("jobAds:eezyAccepted")
        ) : (
          t("jobAds:applied")
        );

      return {
        id: item.id,
        title: (
          <>
            {item.title}
            {item.hasCoverLetter && (
              <IconButton
                onClick={(event) => {
                  event.stopPropagation();
                  openCoverLetterDialog(item.id);
                }}
              >
                <ClipboardIcon style={{ height: "20px", width: "20px" }} />
              </IconButton>
            )}
          </>
        ),
        startDate:
          item.startDate &&
          format(new Date(item.startDate), leadingZerosDateFormat),
        endDate: format(new Date(item.endDate), leadingZerosDateFormat),
        location: item.locations.map((location) => location.city).join(", "),
        customerCompanyName: item.customerCompanyName,
        contactPersonName: item.contactPerson.name,
        status,
        appliedAt:
          item.appliedAt &&
          format(new Date(item.appliedAt), leadingZerosDateFormat),
        url: `/home/jobAds/${item.id}/`,
      } as TalentJobApplicationRowItem;
    },
    [introductionsByBundleId, openCoverLetterDialog, t],
  );

  const groups = useMemo<ItemGroup<TalentJobApplicationRowItem>[]>(
    () => [
      {
        name: "open",
        items: applications.ongoing.map((item) => mapItemToRow(item)),
        status: ItemGroupLoadingState.IDLE,
      },
      {
        name: "ended",
        items: applications.ended.map((item) => mapItemToRow(item)),
        status: ItemGroupLoadingState.IDLE,
      },
    ],
    [applications, mapItemToRow],
  );

  useEffect(() => {
    if (!talentId) return;
    setApplications({ ongoing: [], ended: [] });
    setWithOrderIntroductions([]);
    setHits({ value: 0, relation: Relation.DEFAULT });

    dispatch(fetchTalentJobApplications({ talentId })).then((action) => {
      if (isFulfilled(action)) {
        setApplications(action.payload.data);
        setHits(action.payload.hits);
      }
    });

    dispatch(
      fetchCandidateIntroductions({
        talentId,
        pageSize: "500",
        types: "WithOrder",
        statuses: ALL_WITH_ORDER_INTRODUCTION_STATUSES,
      }),
    ).then((action) => {
      if (isFulfilled(action)) {
        setWithOrderIntroductions(action.payload.data);
      }
    });
  }, [talentId, dispatch]);

  return (
    <>
      <Typography
        variant="header"
        sx={{
          fontWeight: 600,
          marginBottom: "16px",
        }}
      >
        {t("talentProfile:appliedJobs").toUpperCase()}
      </Typography>
      <Box
        sx={{
          background: "#FFFFFF",
          borderRadius: "8px",
        }}
      >
        <GroupedTable
          columns={columns}
          groups={groups}
          itemCount={hits.value}
          pagination={{
            count: hits,
            pageIndex: 0,
            pageSize: 500,
            onPageChange: () => {},
          }}
          translation="jobAds"
          disableSelectAllItems
          disableSelectGroupItems
          disableSelectItems
          hideAllCheckboxes
        />
      </Box>
      <TalentIntroductions
        talentId={talentId}
        appliedBundleIds={appliedBundleIds}
        withOrderIntroductions={withOrderIntroductions}
      />
      <AssignmentCoverLetter
        status={coverLetterModalProps}
        closeDialog={() =>
          setCoverLetterModalProps({
            open: false,
            talentId: "",
            jobAdId: "",
            talentFullName: "",
          })
        }
      />
    </>
  );
};
